import React, { ReactElement } from 'react';
import Button, { ButtonProps } from '@material-ui/core/Button';
import { StandardTextFieldProps } from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import useStyles from './styles';
import TranslatedText from '../../../components/atoms/translated_text';
import Configuration, { Translations } from './configuration';
import * as RegisterSelectors from '../../../redux/reducers/register_reducer/selectors';
import {
  AppSelect,
  AppTextField,
} from '../../../components/molecules/form_controls';
import { Control, Controller, useForm } from 'react-hook-form';
import _ from 'lodash';
import ConfirmationNumberOutlinedIcon from '@material-ui/icons/ConfirmationNumberOutlined';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { StateStatus } from '../../../redux/utils/common';
import RegisterActions from 'redux/reducers/register_reducer/actions';
import { useDispatch } from 'react-redux';
import { LinearProgress } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import GridLayout from 'components/molecules/grid_layout';

const emailPattern =
  /^[-_a-zA-Z0-9][-._a-zA-Z0-9]*@[-._a-zA-Z0-9]+(.[-.a-zA-Z0-9]+)*.(COM|EDU|INFO|GOV|INT|MIL|NET|ORG|BIZ|NAME|MUSEUM|COOP|AERO|PRO|TV|com|edu|info|gov|int|mil|net|org|biz|name|museum|coop|aero|pro|tv|[a-zA-Z]{2})$/;
///^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

interface FormTypes {
  nationalTaxReference: string | null;
  companyName: string | null;
  username: string | null;
  email: string | null;
  confirmEmail: string | null;
  countryOfBusinessId: string | null;
  password: string | null;
  confirmPassword: string | null;
}

// add types or interface
const defaultValues = {
  nationalTaxReference: null,
  companyName: null,
  username: null,
  email: null,
  confirmEmail: null,
  countryOfBusinessId: null,
  password: null,
  confirmPassword: null,
};

interface IProps {
  onSubmit: (data: any) => any;
}

export default function NONEUForm(props: IProps): ReactElement {
  // console.log('Render NONEUForm:');
  const classes = useStyles();
  const dispatch = useDispatch();
  const { getValues, watch, errors, control, handleSubmit } =
    useForm<FormTypes>({
      defaultValues,
    });

  React.useEffect(() => {
    dispatch(RegisterActions.fetchCountriesAction());
  }, [dispatch]);

  const watchEmail = watch('email');
  const watchPassword = watch('password');
  const watchConfirmPassword = watch('confirmPassword');
  const watchEmailConfirm = watch('confirmEmail');
  const registerStatus = RegisterSelectors.useGetRegisterStatus();

  return (
    <form
      className={classes.form}
      onSubmit={handleSubmit(props.onSubmit)}
      noValidate
    >
      <GridLayout
        elements={[
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                error={'companyName' in errors}
                helperText={
                  errors.companyName ? errors.companyName.message : ' '
                }
                rules={{
                  required: 'Required Field',
                }}
                required
                fullWidth
                id="companyName"
                control={control}
                label="Company Name"
                autoComplete="companyName"
                name="companyName"
                icon={{
                  position: 'left',
                  Component: <BusinessIcon className={classes.inputIcon} />,
                }}
              />
            ),
            size: 12,
            id: 'companyName',
          },
          {
            element: (
              <CountriesSelect
                disabled={registerStatus === StateStatus.Pending}
                control={control}
                error={'countryOfBusinessId' in errors}
                name="countryOfBusinessId"
                helperText={
                  errors.countryOfBusinessId
                    ? errors.countryOfBusinessId.message
                    : ' '
                }
                rules={{
                  required: 'Required Field',
                }}
                label="Country of Business"
              />
            ),
            size: 6,
            id: 'countryOfBusinessId',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="nationalTaxReference"
                control={control}
                label="Tax Reference Number"
                name="nationalTaxReference"
                error={'nationalTaxReference' in errors}
                helperText={
                  errors.nationalTaxReference
                    ? errors.nationalTaxReference.message
                    : ' '
                }
                rules={{
                  required: 'Required Field',
                }}
                autoComplete="nationalTaxReference"
                icon={{
                  position: 'left',
                  Component: (
                    <ConfirmationNumberOutlinedIcon
                      className={classes.inputIcon}
                    />
                  ),
                }}
              />
            ),
            size: 6,
            id: 'nationalTaxReference',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                fullWidth
                id="username"
                label="Username"
                name="username"
                required
                helperText={errors.username ? errors.username.message : ' '}
                rules={{
                  required: 'Required Field',
                }}
                error={'username' in errors}
                autoComplete="username"
                control={control}
                icon={{
                  position: 'left',
                  Component: (
                    <PeopleAltOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
              />
            ),
            size: 12,
            id: 'username',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                control={control}
                error={
                  'password' in errors ||
                  (watchPassword ? watchPassword.length < 8 : false)
                }
                rules={{
                  required: 'Required Field',
                  minLength: {
                    value: 8,
                    message: Translations.password_length_error.English,
                  },
                }}
                helperText={
                  (errors.password &&
                    errors.password.type === 'required' &&
                    errors.password.message) ||
                  (watchPassword &&
                    watchPassword.length < 8 &&
                    Translations.password_length_error.English) ||
                  ' '
                }
                name="password"
                label="Password"
                type="password"
                id="password"
                icon={{
                  position: 'left',
                  Component: <LockOutlinedIcon className={classes.inputIcon} />,
                }}
              />
            ),
            size: 6,
            id: 'password',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                name="confirmPassword"
                error={
                  'confirmPassword' in errors ||
                  (watchPassword && watchConfirmPassword
                    ? watchPassword !== watchConfirmPassword
                    : false)
                }
                label="Confirm Password"
                helperText={
                  (errors.confirmPassword &&
                    errors.confirmPassword.type === 'required' &&
                    errors.confirmPassword.message) ||
                  (watchPassword &&
                    watchConfirmPassword &&
                    watchPassword !== watchConfirmPassword &&
                    Translations.password_match_error.English) ||
                  ' '
                }
                type="password"
                id="confirmPassword"
                control={control}
                rules={{
                  required: 'Required Field',
                  validate: () =>
                    getValues('password') === getValues('confirmPassword') ||
                    Translations.password_match_error.English,
                }}
                icon={{
                  position: 'left',
                  Component: <LockOutlinedIcon className={classes.inputIcon} />,
                }}
              />
            ),
            size: 6,
            id: 'confirmPassword',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                control={control}
                error={
                  'email' in errors ||
                  (watchEmail ? !watchEmail?.match(emailPattern) : false)
                }
                helperText={
                  errors.email && errors.email.type === 'required'
                    ? errors.email.message
                    : watchEmail && !watchEmail?.match(emailPattern)
                    ? Translations.invalid_email.English
                    : ' '
                }
                rules={{
                  required: 'Required Field',
                  pattern: {
                    value: emailPattern,
                    message: Translations.invalid_email.English,
                  },
                }}
                icon={{
                  position: 'left',
                  Component: (
                    <EmailOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
              />
            ),
            size: 6,
            id: 'email',
          },
          {
            element: (
              <AppTextField
                InputLabelProps={{
                  classes: {
                    root: classes.inputLabel,
                    focused: classes.inputLabelFocus,
                  },
                }}
                InputProps={{
                  classes: {
                    focused: classes.notchedOutlineFocus,
                    notchedOutline: classes.notchedOutline,
                  } as any,
                  className: classes.input,
                }}
                disabled={registerStatus === StateStatus.Pending}
                variant="outlined"
                // margin="normal"
                required
                fullWidth
                id="confirmEmail"
                label="Confirm Email Address"
                name="confirmEmail"
                autoComplete="confirmEmail"
                control={control}
                error={
                  'confirmEmail' in errors ||
                  (watchEmailConfirm
                    ? !watchEmailConfirm?.match(emailPattern)
                    : false) ||
                  (watchEmail && watchEmailConfirm
                    ? watchEmail !== watchEmailConfirm
                    : false)
                }
                helperText={
                  errors.email && errors.email.type === 'required'
                    ? errors.email.message
                    : (watchEmail &&
                        ((!watchEmailConfirm?.match(emailPattern) &&
                          Translations.invalid_email.English) ||
                          (watchEmail != watchEmailConfirm &&
                            Translations.email_match_error.English))) ||
                      ' '
                }
                rules={{
                  required: 'Required Field',
                  validate: () =>
                    getValues('email') === getValues('confirmEmail') ||
                    Translations.email_match_error.English,
                }}
                icon={{
                  position: 'left',
                  Component: (
                    <EmailOutlinedIcon className={classes.inputIcon} />
                  ),
                }}
              />
            ),
            size: 6,
            id: 'confirmEmail',
          },
        ]}
      />

      {registerStatus === StateStatus.Pending && <LinearProgress />}
      <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <SubmitWrapper
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          className={classes.submit}
          rstatus={registerStatus}
        >
          <TranslatedText textMap={Translations.form_button_register} />
        </SubmitWrapper>
      </Box>
    </form>
  );
}

interface IButtonProps extends ButtonProps {
  rstatus: StateStatus;
}

const SubmitWrapper = (props: IButtonProps) => {
  return <Button {...props} disabled={props.rstatus === StateStatus.Pending} />;
};

interface ICountriesSelectProps {
  control: Control;
  rules: any;
  name: string;
  label: string;
  helperText: string | undefined;
  error: boolean | undefined;
  disabled: boolean;
}

const CountriesSelect = (props: ICountriesSelectProps) => {
  const classes = useStyles();
  const { data } = RegisterSelectors.useGetCountries();
  const options: Array<{ id: string; label: string }> | null = Array.isArray(
    data
  )
    ? data.map((element) => ({
        id: element.country_id,
        label: element.description,
      }))
    : null;

  return (
    <AppSelect
      InputLabelProps={{
        classes: {
          root: classes.inputLabel,
          focused: classes.inputLabelFocus,
        },
      }}
      SelectProps={{
        classes: { icon: classes.selectIcon },
      }}
      InputProps={{
        classes: {
          focused: classes.notchedOutlineFocus,
          notchedOutline: classes.notchedOutline,
        } as any,
        className: classes.input,
      }}
      disabled={props.disabled}
      // margin="normal"
      fullWidth
      label={props.label}
      name={props.name}
      required
      error={props.error}
      control={props.control}
      helperText={props.helperText}
      rules={props.rules}
      autoComplete={props.name}
      variant="outlined"
      icon={{
        position: 'left',
        Component: <ClearAllIcon className={classes.inputIcon} />,
      }}
      options={options || []}
    />
  );
};
